import { useMemo, useState } from "react";
import { AlertTriangle, Check, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import type { DashboardData, Owner } from "../../types";
import { money, shortName } from "../../lib/finance";
import { Private } from "../../components/Privacy";
import Modal from "../../components/Modal";
import {
  pendingThrough,
  reconciliationMonthLabel,
  reconciliationState,
} from "../../lib/reconciliation";
import LoadingLabel from "../../components/LoadingLabel";

const post = (path: string, body: unknown) =>
  api(`reconciliations/${path}`, body);

export interface ReconciliationReviewProps {
  data: DashboardData;
  month: string;
  profile: Owner;
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
  onOpenAccount: (accountId: string) => void;
  onReviewPending: () => void;
}

export default function ReconciliationReview({
  data,
  month,
  profile: _profile,
  open,
  onClose,
  onSaved,
  onOpenAccount,
  onReviewPending,
}: ReconciliationReviewProps) {
  const state = useMemo(() => reconciliationState(data, month), [data, month]);
  const pending = useMemo(() => pendingThrough(data, month), [data, month]);
  const [skipping, setSkipping] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const monthLabel = reconciliationMonthLabel(month);

  if (!open) return null;

  async function save(accountId: string, status: "Confirmed" | "Skipped") {
    const item = state.items.find((row) => row.account.id === accountId);
    if (!item) return;
    setBusy(accountId);
    setError("");
    try {
      await post("confirm", {
        month,
        accountId,
        status,
        confirmedBalance: item.expected,
        note: status === "Skipped" ? note : "",
      });
      setSkipping(null);
      setNote("");
      await onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy("");
    }
  }

  async function complete() {
    setBusy("complete");
    setError("");
    try {
      await post("complete", { month });
      await onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy("");
    }
  }

  return (
    <Modal
      title={`Reconcile ${monthLabel}`}
      onClose={onClose}
      className="reconciliation-modal"
    >
      <div style={{ marginTop: -8, marginBottom: 16 }}>
        <span className="eyebrow">MONTH-END REVIEW</span>
        <p className="muted" style={{ margin: "4px 0 0", fontSize: "0.84rem" }}>
          Compare each closing balance with its bank, card or counted cash balance.
        </p>
      </div>

      {error && <div className="inline-error" role="alert">{error}</div>}

        {pending.length > 0 && (
          <div className="pending-notice" role="alert">
            <p>
              <strong>
                {pending.length} pending {pending.length === 1 ? "transaction" : "transactions"} to settle first.
              </strong>{" "}
              Mark each cleared once the bank settles it, or void it if it never does, then reconcile.
            </p>
            <div className="detail-actions">
              <button type="button" onClick={onReviewPending}>
                Review pending
              </button>
            </div>
          </div>
        )}

        <div className="reconciliation-progress">
          <span>
            {state.items.filter((item) => item.valid).length} of {state.items.length} accounts resolved
          </span>
          <strong>{state.complete ? "Reconciled" : "In progress"}</strong>
        </div>

        <div className="reconciliation-list">
          {state.items.map(({ account, expected, saved, valid }) => (
            <article
              className={`reconciliation-account${valid ? " resolved" : ""}`}
              key={account.id}
            >
              <div className="reconciliation-account-head">
                <span>
                  <strong>{shortName(account.name)}</strong>
                  <small>
                    {account.owner} · {account.type} · {account.currency}
                  </small>
                </span>
                {valid ? (
                  <span className="reconciliation-status">
                    <Check size={15} />
                    {saved?.status}
                  </span>
                ) : saved ? (
                  <span className="reconciliation-status stale">
                    <AlertTriangle size={15} />
                    Changed
                  </span>
                ) : null}
              </div>

              <div className="reconciliation-balance">
                <span>Barnyard closing balance</span>
                <strong>
                  <Private>{money(expected, account.currency)}</Private>
                </strong>
              </div>

              {valid ? (
                <small>
                  Confirmed by {saved?.confirmedBy}
                  {saved?.note ? ` · ${saved.note}` : ""}
                </small>
              ) : skipping === account.id ? (
                <div className="reconciliation-skip">
                  <label>
                    Why is this account skipped?
                    <input
                      autoFocus
                      value={note}
                      maxLength={300}
                      onChange={(event) => setNote(event.target.value)}
                      placeholder="e.g. Cash not counted"
                    />
                  </label>
                  <div>
                    <button
                      type="button"
                      className="text-button"
                      onClick={() => {
                        setSkipping(null);
                        setNote("");
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="secondary-action"
                      disabled={busy === account.id || !note.trim()}
                      onClick={() => void save(account.id, "Skipped")}
                    >
                      {busy === account.id ? <LoadingLabel>Saving</LoadingLabel> : "Save reason"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="reconciliation-actions">
                  <button
                    type="button"
                    className="secondary-action"
                    disabled={busy === account.id || pending.length > 0}
                    onClick={() => void save(account.id, "Confirmed")}
                  >
                    {busy === account.id ? <LoadingLabel>Checking</LoadingLabel> : "Looks right"}
                  </button>
                  <button
                    type="button"
                    className="text-button"
                    onClick={() => onOpenAccount(account.id)}
                  >
                    Fix entries <ChevronRight size={14} />
                  </button>
                  <button
                    type="button"
                    className="text-button subtle"
                    onClick={() => setSkipping(account.id)}
                  >
                    Skip
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>

        <div className="reconciliation-footer">
          <p>
            {state.items.every((item) => item.valid)
              ? "Every active account is resolved. Either profile can finish the month."
              : "Resolve every active account. You can close this review and return later."}
          </p>
          <button
            type="button"
            className="primary-button"
            disabled={
              busy === "complete" ||
              pending.length > 0 ||
              !state.items.length ||
              state.items.some((item) => !item.valid)
            }
            onClick={() => void complete()}
          >
            {busy === "complete" ? (
              <LoadingLabel>Reconciling</LoadingLabel>
            ) : state.complete ? (
              "Month reconciled"
            ) : (
              "Reconcile month"
            )}
          </button>
        </div>
    </Modal>
  );
}
export { default as ReconciliationCard } from "./ReconciliationCard";
